import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { asyncScheduler, BehaviorSubject, map, Observable, observeOn, Subject } from 'rxjs';
import { LoaderEnum } from '../enum/loader.enum';
import { LoaderState } from '../model/loading/loader-state.model';
import { ILoaderConfig, LoaderConfig } from '../model/loading/loading-config';
import { LoaderStatus } from '../model/loading/loading-status.enum';
import { WcagActionEnum, WcagService } from '@lib/core';

@Injectable({
  providedIn: 'root'
})
export class LoaderService implements OnDestroy {
  private loaderState: BehaviorSubject<LoaderState> = new BehaviorSubject<LoaderState>(null);
  public loaderState$: Observable<LoaderState> = this.loaderState.asObservable();

  private loaderMapState: BehaviorSubject<Map<string, LoaderStatus>> = new BehaviorSubject<Map<string, LoaderStatus>>(new Map());
  public loaderMapState$: Observable<Map<string, LoaderStatus>> = this.loaderMapState.asObservable();

  public loaderConfig: ILoaderConfig;
  private loaderStatusMap = new Map<string, LoaderStatus>();
  private statusMapSubject = new BehaviorSubject<Map<string, LoaderStatus>>(this.loaderStatusMap);

  private hideLoaderTimers = new Map<string, ReturnType<typeof setTimeout>>();

  private destroy$ = new Subject<void>();

  constructor(
    private wcagService: WcagService,
    @Optional() @Inject('loaderConfig') private config: ILoaderConfig
  ) {
    this.loaderConfig = this.config || new LoaderConfig();
  }

  public showLoader(id: string): void {
    this.setLoaderStatus(id, LoaderStatus.pending);
    this.wcagService.announceAction(WcagActionEnum.DATA_LOADING_STARTED);

    this.loaderState.next(new LoaderState(id, true));
    this.emitMapState();
  }

  public hideLoader(id: string, timeout: number = 50): void {
    this.setLoaderStatus(id, LoaderStatus.stopped);
    this.wcagService.announceAction(WcagActionEnum.DATA_LOADING_COMPLETED);

    const normalizedId = id.toLowerCase();
    const timerId = setTimeout(() => {
      this.finalizeHideLoader(normalizedId);
    }, timeout);
    this.hideLoaderTimers.set(normalizedId, timerId);
  }

  public setLoaderStatus(id: string | LoaderEnum, status: LoaderStatus): void {
    const key = id.toLowerCase();
    this.clearTimerForLoader(key);

    this.loaderStatusMap.set(key, status);
    this.statusMapSubject.next(new Map(this.loaderStatusMap));
  }

  public isLoaded(id?: string | LoaderEnum): boolean {
    if (!id) return false;

    const status = this.loaderStatusMap.get(id.toLowerCase());
    return status !== LoaderStatus.pending;
  }

  public isLoading$(id: string | LoaderEnum): Observable<boolean> {
    const key = id.toLowerCase();
    return this.statusMapSubject.asObservable().pipe(
      map((statusMap) => statusMap.get(key) === LoaderStatus.pending),
      observeOn(asyncScheduler)
    );
  }

  public isLoading(id?: string | LoaderEnum): boolean {
    if (!id) return false;

    const status = this.loaderStatusMap.get(id.toLowerCase());
    return status === LoaderStatus.pending;
  }

  public clearLoader(id: string): void {
    const normalizedId = id.toLowerCase();
    this.clearTimerForLoader(normalizedId);
    this.loaderStatusMap.delete(normalizedId);
    this.emitMapState();
  }

  public clearAllLoaders(): void {
    this.hideLoaderTimers.forEach((timer) => clearTimeout(timer));
    this.hideLoaderTimers.clear();
    this.loaderStatusMap.clear();
    this.emitMapState();
  }

  public getLoaderStatus(id: string = LoaderEnum.MAIN): LoaderStatus | undefined {
    return this.loaderStatusMap.get(id.toLowerCase()) ?? LoaderStatus.pending;
  }

  public hasActiveLoaders(): boolean {
    return Array.from(this.loaderStatusMap.values()).some((status) => status === LoaderStatus.pending);
  }

  private clearTimerForLoader(id: string): void {
    const existingTimer = this.hideLoaderTimers.get(id);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.hideLoaderTimers.delete(id);
    }
  }

  private finalizeHideLoader(id: string): void {
    this.loaderState.next(new LoaderState(id, false));
    this.hideLoaderTimers.delete(id);
    this.emitMapState();
  }

  private emitMapState(): void {
    this.loaderMapState.next(new Map(this.loaderStatusMap));
  }

  ngOnDestroy(): void {
    this.hideLoaderTimers.forEach((timer) => clearTimeout(timer));
    this.hideLoaderTimers.clear();

    this.destroy$.next();
    this.destroy$.complete();
    this.loaderState.complete();
    this.loaderMapState.complete();
  }
}
